import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { SV_SOJ_ORCMOCK_ADDRESS } from "../../constants";
import ReactMarkdown from "react-markdown";


// Cosmos Orchestration Agent streaming endpoint
const SVR_URL = `${SV_SOJ_ORCMOCK_ADDRESS}/svragentchat-stream`;

function Chat() {

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  /*
   * Generate one CID when the Chat component is created.
   *
   * React re-renders:
   *     same CID
   *
   * Browser refresh:
   *     new CID
   */
  const [cid] = useState(() => uuidv4());


  const handleSend = async () => {

    if (!input.trim()) {
      return;
    }

    const userMessage = input.trim();

    /*
     * Add user message
     */
    setMessages(prev => [
      ...prev,
      {
        role: "user",
        content: userMessage
      }
    ]);


    /*
     * Add empty assistant message.
     *
     * The streaming response will update
     * this message as data arrives.
     */
    setMessages(prev => [
      ...prev,
      {
        role: "assistant",
        content: ""
      }
    ]);


    try {

      /*
       * Send the same CID with every message
       * during this Chat session.
       */
      await sendMessage(
        cid,
        userMessage,
        setMessages
      );

    } catch (err) {

      console.error(
        "Cosmos chat error:",
        err
      );

      /*
       * Replace the empty assistant message
       * with an error message.
       */
      setMessages(prev => {

        const updated = [...prev];

        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content: "Unable to get response from Customer Support."
        };

        return updated;
      });
    }


    setInput("");
  };


  return (

    <div
      style={{
        width: "500px",
        margin: "20px auto"
      }}
    >

      {/* Chat messages */}

      <div
        style={{
          border: "1px solid #ccc",
          padding: "10px",
          height: "400px",
          overflowY: "auto"
        }}
      >

        {messages.map((msg, i) => (

          <div
            key={i}
            style={{
              margin: "10px 0"
            }}
          >

            <strong>
              {msg.role === "user"
                ? "You"
                : "Customer Support"}:
            </strong>

            <div>
                <ReactMarkdown>
                  {msg.content}
                </ReactMarkdown>
            </div>

          </div>

        ))}

      </div>


      {/* User input */}

      <input
        value={input}

        onChange={(e) =>
          setInput(e.target.value)
        }

        onKeyDown={(e) => {

          if (e.key === "Enter") {
            handleSend();
          }

        }}

        style={{
          width: "60%"
        }}
      />


      <button
        onClick={handleSend}
      >
        Send
      </button>

    </div>
  );
}


/*
 * Send message to Cosmos Orchestration Agent
 * and process the streaming response.
 */
async function sendMessage(
  cid,
  message,
  setMessages
) {

  console.log(
    "Sending message:",
    {
      cid,
      message
    }
  );


  const res = await fetch(
    SVR_URL,
    {

      method: "POST",

      headers: {
        "Content-Type": "application/json"
      },

      /*
       * Request sent to Node:
       *
       * {
       *    cid: "...",
       *    message: "..."
       * }
       */
      body: JSON.stringify({
        cid,
        message
      })
    }
  );


  if (!res.ok) {

    throw new Error(
      `Server returned ${res.status} ${res.statusText}`
    );

  }


  if (!res.body) {

    throw new Error(
      "Streaming response body is missing"
    );

  }


  const reader =
    res.body.getReader();


  const decoder =
    new TextDecoder("utf-8");


  let buffer = "";

  let assistantText = "";


  /*
   * Read streaming response
   */
  while (true) {

    const {
      done,
      value
    } = await reader.read();


    if (done) {
      break;
    }


    /*
     * Decode current chunk.
     */
    buffer += decoder.decode(
      value,
      {
        stream: true
      }
    );


    /*
     * Cosmos server is returning
     * newline-delimited raw text.
     *
     * It is NOT SSE "data:" format.
     */
    const lines =
      buffer.split("\n");


    /*
     * Keep the last incomplete line.
     */
    buffer =
      lines.pop();


    /*
     * Process complete lines.
     */
    for (const line of lines) {

      if (!line.trim()) {
        continue;
      }


      assistantText +=
        line + "\n";


      /*
       * Update the last assistant message.
       */
      setMessages(prev => {

        const updated =
          [...prev];


        updated[
          updated.length - 1
        ] = {

          ...updated[
            updated.length - 1
          ],

          content:
            assistantText
        };


        return updated;

      });

    }

  }


  /*
   * Flush TextDecoder.
   */
  buffer +=
    decoder.decode();


  /*
   * Add any remaining text.
   */
  if (buffer) {

    assistantText +=
      buffer;


    setMessages(prev => {

      const updated =
        [...prev];


      updated[
        updated.length - 1
      ] = {

        ...updated[
          updated.length - 1
        ],

        content:
          assistantText
      };


      return updated;

    });

  }

}


export default Chat;